/**
 * Разметка документа Word, приведённая к тому, что можно показать.
 *
 * **Почему фильтр, а не доверие.** Разметку строит `mammoth` из содержимого `.docx`,
 * то есть из файла, который юристу прислал кто угодно — противная сторона, суд,
 * незнакомый человек почтой. Внутри `.docx` есть ссылки, а ссылка бывает
 * `javascript:`; со временем библиотека научится новым тегам, и её вывод перестанет
 * быть тем, на что мы смотрели сегодня.
 *
 * **Как устроено.** Разрешённый тег собирается заново — из имени и проверенных
 * значений, — а не подчищается на месте. Поэтому ни один кусок исходной строки
 * атрибутом не становится: нечего экранировать и нечего обходить. Неразрешённый тег
 * снимается, а его текст остаётся: выбросить абзац из документа хуже, чем показать
 * его без оформления.
 */

/** Теги, из которых состоит документ. Всё остальное — оформление, без которого можно. */
const ALLOWED = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'sup', 'sub',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'a', 'img',
]);

/** Теги без закрывающей пары: собираются как `<br />`. */
const VOID = new Set(['br', 'hr', 'img']);

/** Ссылка ведёт наружу или в почту. `javascript:` и `data:` ссылкой не бывают. */
const SAFE_LINK = /^(?:https?:\/\/|mailto:|tel:)/i;

/** Картинка внутри `.docx` приходит вложенной в сам файл — другого источника нет. */
const SAFE_IMAGE = /^data:image\/(?:png|jpeg|gif|bmp|webp|tiff);base64,[a-z0-9+/=\s]*$/i;

/** Все атрибуты тега разом: одинарные кавычки, двойные и без кавычек. */
const ATTRIBUTE = /([a-z][a-z0-9-]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/gi;

const attributesOf = (tag: string): Map<string, string> => {
  const found = new Map<string, string>();

  for (const match of tag.matchAll(ATTRIBUTE)) {
    const [, name, , double, single, bare] = match;
    if (name) found.set(name.toLowerCase(), (double ?? single ?? bare ?? '').trim());
  }
  return found;
};

/** Кавычки и угловые скобки в значении: собранный тег обязан остаться одним тегом. */
const quoted = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Атрибуты, которые переживают сборку. Их всего четыре, и каждый проверен. */
function keptAttributes(name: string, tag: string): string {
  const given = attributesOf(tag);
  const kept: string[] = [];

  if (name === 'a') {
    const href = given.get('href');
    // Ссылка, ведущая непонятно куда, показывается текстом: снимаем только адрес.
    if (href && SAFE_LINK.test(href)) {
      kept.push(`href="${quoted(href)}" target="_blank" rel="noopener noreferrer"`);
    }
  }

  if (name === 'img') {
    const source = given.get('src');
    if (!source || !SAFE_IMAGE.test(source)) return '';

    kept.push(`src="${quoted(source)}"`);
    const alt = given.get('alt');
    if (alt) kept.push(`alt="${quoted(alt)}"`);
  }

  if (name === 'td' || name === 'th') {
    for (const span of ['colspan', 'rowspan']) {
      const value = given.get(span);
      // Объединённые клетки таблицы — часть смысла документа, а не оформления.
      if (value && /^\d{1,3}$/.test(value)) kept.push(`${span}="${value}"`);
    }
  }

  return kept.length > 0 ? ` ${kept.join(' ')}` : '';
}

/**
 * Оставляет от разметки только то, что показывает документ.
 *
 * Возвращается строка, готовая к вставке: за пределами разрешённого списка
 * из неё не выживает ничего — ни скрипты, ни обработчики, ни чужие атрибуты.
 */
export function sanitizeWordHtml(html: string): string {
  // Комментарий может нести незакрытую скобку и склеить два тега в один.
  const withoutComments = html.replace(/<!--[\s\S]*?-->/g, '');

  return withoutComments.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (tag, rawName: string) => {
    const name = rawName.toLowerCase();
    if (!ALLOWED.has(name)) return '';

    if (tag.startsWith('</')) return VOID.has(name) ? '' : `</${name}>`;

    // Картинка без пригодного источника — не пустая рамка, а ничего.
    if (name === 'img') {
      const attributes = keptAttributes(name, tag);
      return attributes === '' ? '' : `<img${attributes} />`;
    }

    const attributes = keptAttributes(name, tag);
    return VOID.has(name) ? `<${name} />` : `<${name}${attributes}>`;
  });
}
